import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default theme colors - Professional blue palette
const defaultTheme = {
  primary: '#0077B6',
  primaryLight: '#B6D0E2',
  accent: '#00B4D8',
  accentLight: '#D4EDBC',
  background: '#FFFFFF',
  card: '#FFFFFF',
  text: '#0F172A',
  textSecondary: '#64748B',
  border: '#E2E8F0',
  // Badge colors
  badgeDestacado: '#8B5CF6',
  badgeOferta: '#F97316',
  badgePack: '#06B6D4',
  discountBadge: '#10B981',
  // UI colors
  buttonHover: '#005F92',
  inputBorder: '#9CA3AF',
  inputBackground: '#FFFFFF',
  separatorColor: '#E5E7EB',
  // Typography
  fontFamily: 'Public Sans',
  headingFontFamily: 'Barlow Condensed',
  buttonStyle: 'rounded'
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      const response = await axios.get(`${API}/theme-settings`);
      const settings = response.data;
      setTheme({
        ...defaultTheme,
        primary: settings.primaryColor || defaultTheme.primary,
        primaryLight: lightenColor(settings.primaryColor || defaultTheme.primary, 0.3),
        accent: settings.accentColor || defaultTheme.accent,
        accentLight: lightenColor(settings.accentColor || defaultTheme.accent, 0.3),
        background: settings.backgroundColor || defaultTheme.background,
        card: settings.cardBackground || defaultTheme.card,
        // Badge colors
        badgeDestacado: settings.badgeDestacadoColor || defaultTheme.badgeDestacado,
        badgeOferta: settings.badgeOfertaColor || defaultTheme.badgeOferta,
        badgePack: settings.badgePackColor || defaultTheme.badgePack,
        discountBadge: settings.discountBadgeColor || defaultTheme.discountBadge,
        // UI colors
        buttonHover: settings.buttonHoverColor || defaultTheme.buttonHover,
        text: settings.textColor || defaultTheme.text,
        textSecondary: settings.textSecondaryColor || defaultTheme.textSecondary,
        border: settings.borderColor || defaultTheme.border,
        inputBorder: settings.inputBorderColor || defaultTheme.inputBorder,
        inputBackground: settings.inputBackgroundColor || defaultTheme.inputBackground,
        separatorColor: settings.separatorColor || defaultTheme.separatorColor,
        // Typography
        fontFamily: settings.fontFamily || defaultTheme.fontFamily,
        headingFontFamily: settings.headingFontFamily || defaultTheme.headingFontFamily,
        buttonStyle: settings.buttonStyle || defaultTheme.buttonStyle
      });
    } catch (error) {
      console.error('Error fetching theme:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to lighten a hex color
  const lightenColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent * 100);
    const R = Math.min(255, (num >> 16) + amt);
    const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
    const B = Math.min(255, (num & 0x0000FF) + amt);
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  };

  const refreshTheme = () => {
    fetchTheme();
  };

  return (
    <ThemeContext.Provider value={{ theme, loading, refreshTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
